import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Header from '@/common/header';
import { LinksMirrorDownload } from '@/common/link-mirror-download';
import { AuthLoadingOverlay, SsoProviders } from '@/components/modals/sso';
import type { AlertStateOrEmpty } from '@/components/ui/alert';
import { Alert } from '@/components/ui/alert';
import CaptchaProvider from '@/components/ui/captcha-provider';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { useTrackSoftwareDownload } from '@/hooks/use-track-software-download';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { AlreadyLoggedInNotice } from '../already-logged-in-notice';
import type { DefaultTrialProps } from '../trial-layout-switcher';
import styles from './matrix/matrix.module.css';
import { Multiconnection } from './matrix/multiconnection/multiconnection';
import { Closing, MatrixFooter } from './matrix/sections/closing';
import { Download } from './matrix/sections/download';
import { Faq } from './matrix/sections/faq';
import { Games } from './matrix/sections/games';
import { Hero } from './matrix/sections/hero';
import { ProofClips } from './matrix/sections/proof-clips';
import { Pros } from './matrix/sections/pros';
import { Servers } from './matrix/sections/servers';
import { cls, cx, FORM_ID, m, prefersReducedMotion } from './matrix/shared';
import { SignupField } from './matrix/signup-field';
import { TrialDialog } from './matrix/trial-dialog';

interface FormData {
  name: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

type FieldName = Exclude<keyof FormData, 'recaptcha'>;
const FIELD_NAMES: FieldName[] = ['name', 'email', 'repeatEmail', 'password', 'passwordRepeat'];
// The five titles right under the button: the "does it run MY game?" answer (names stay canonical).
const FORM_GAMES = ['Valorant', 'CS2', 'League of Legends', 'Fortnite', 'Apex Legends'];

// Any link to the site's trial page (the shared header's pill and its mobile sheet) opens this page's own form.
const TRIAL_PATH = /^\/(?:[a-z]{2}\/)?trial\/?$/i;

interface MatrixTrialProps extends DefaultTrialProps {}

export const Matrix = ({ trialConfigId, trialPageId, isDisabledButton = false }: MatrixTrialProps) => {
  const { t } = useTranslation(['pages/trial-matrix', 'common', 'components/player-box', 'components/faq']);
  const urlDownload = LinksMirrorDownload();
  const trackDownload = useTrackSoftwareDownload('trial_matrix');
  const auth = useAuth();
  const { user } = auth;
  const router = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  const heroSlotRef = useRef<HTMLDivElement>(null);
  const [loading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [heroSlotHeight, setHeroSlotHeight] = useState<number | null>(null);
  const [popUp, setPopUp] = useState<{
    show: boolean;
    message: string;
    state: AlertStateOrEmpty;
  }>({ show: false, message: '', state: '' });

  const schema = yup.object().shape({
    name: personNameSchema(t('first_section.form.errors.mensage_error_empty_name'), t('first_section.form.errors.mensage_error_invalid_name')),
    email: yup.string().email(t('first_section.form.errors.mensage_error_valid_email')).required(t('first_section.form.errors.mensage_error_empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('first_section.form.errors.mensage_error_valid_email'))
      .required(t('first_section.form.errors.mensage_error_empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('first_section.form.errors.mensage_error_repeat_email')),
    password: yup.string().required(t('first_section.form.errors.mensage_error_empty_password')).min(6, t('first_section.form.errors.mensage_error_short_password')),
    passwordRepeat: yup
      .string()
      .required(t('first_section.form.errors.mensage_error_empty_password'))
      .min(6, t('first_section.form.errors.mensage_error_short_password'))
      .oneOf([yup.ref('password')], t('first_section.form.errors.mensage_error_repeat_password')),
    recaptcha: yup.string().required(t('first_section.form.errors.mensage_error_empty_recaptcha')),
  });

  async function handleLogin({ email, password, recaptcha }: { email: string; password: string; recaptcha: string }) {
    // Awaited, not raced: a logout landing after this login would end the new session. Its result does not
    // gate the login, which replaces whoever the server session held.
    if (user) await auth.logout();
    // eslint-disable-next-line func-names
    auth.login({ login: email, password, recaptcha }, function () {});
  }

  const {
    register,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { errors, touchedFields, isSubmitted },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      repeatEmail: '',
      password: '',
      passwordRepeat: '',
      recaptcha: '',
    },
    // The design's rhythm: a field is judged once it has been left, then re-judged on every keystroke.
    mode: 'onTouched',
  });

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required' });
  }, [setValue, setError]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    if (typeof window !== 'undefined' && (window as any).captchaReset) {
      (window as any).captchaReset();
    }
  }, [setValue]);

  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const handleSsoStart = useCallback(() => {
    setIsSsoAuthenticating(true);
  }, []);
  const handleSsoSuccess = useCallback(() => {
    setIsSsoAuthenticating(false);
  }, []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    const utm = getUtmData();
    const body: CreateUserTrialParams = {
      name: data.name,
      email: data.email,
      password: data.password,
      recaptcha: data.recaptcha,
      telegram: null,
      password_repeat: data.passwordRepeat,
      ref: '',
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      custom: trialConfigId,
      trialPageId,
      ...utm,
    };

    auth.createTrial(
      {
        ...body,
      },
      (error) => {
        // Reset captcha on error
        setValue('recaptcha', '');
        if (typeof window !== 'undefined' && (window as any).captchaReset) {
          (window as any).captchaReset();
        }
        setPopUp({
          show: true,
          message: t(apiErrorMessageKey(error, { fallbackKey: 'first_section.form.errors.message_error_default_create_account', statusKeys: TRIAL_STATUS_KEYS })),
          state: 'error',
        });
        setIsLoading((prevState) => !prevState);
      },
      () => {
        handleLogin({ email: data.email, password: data.password, recaptcha: data.recaptcha });
        sendGTMEvent({ event: 'createAccount' });
        sessionStorage.setItem('customTrialApproved', 'true');
        router.push('/thanks-for-trial-no-card');
      },
      /* showSuccessToast */ false
    );
  };

  const handleDownloadClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      // Tracking must not hold the installer navigation.
      setTimeout(() => {
        window.location.href = urlDownload;
      }, 300);
      trackDownload(urlDownload);

      fetch('/api/ga4-download', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          events: [
            {
              name: 'download_click',
              params: {
                idioma_navegador: navigator?.language || 'NP_UNKNOWN_NP',
                pais: 'NP_UNKNOWN_NP', // será sobrescrito no backend
                origem: sessionStorage.getItem('idref') || 'NP_UNKNOWN_NP',
                ip_do_usuario_no_site: 'NP_UNKNOWN_NP', // será sobrescrito no backend
              },
            },
          ],
        }),
      }).catch(() => {});
    },
    [trackDownload, urlDownload]
  );

  // ---- trial CTAs: every one opens the form in the modal; without <dialog> support, scroll to the hero form ----
  const scrollToForm = useCallback(() => {
    const form = document.getElementById(FORM_ID);
    if (!form) return;
    form.scrollIntoView?.({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    window.setTimeout(() => form.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true }), 450);
  }, []);

  const openTrial = useCallback(() => {
    const supportsDialog = typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function';
    if (!supportsDialog) {
      scrollToForm();
      return;
    }
    // the hero keeps the form's height while the form lives in the dialog, so nothing behind the backdrop moves
    setHeroSlotHeight(heroSlotRef.current?.getBoundingClientRect().height ?? null);
    setValue('recaptcha', '');
    setDialogOpen(true);
  }, [scrollToForm, setValue]);

  const handleTrialClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      openTrial();
    },
    [openTrial]
  );

  // The form moves between the hero and the dialog: a fresh captcha widget mounts there, so the old token is void.
  // Cleared BEFORE the move, never in an effect after it: the new widget's own mount effect (the bypass token in
  // exempt countries) runs first and would be wiped.
  const closeDialog = useCallback(() => {
    setValue('recaptcha', '');
    setDialogOpen(false);
  }, [setValue]);

  // In-page links to /trial open the form in place instead of reloading the page the visitor is already on.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      // Only the LP's own CTAs: the site header's "Teste grátis" keeps its site-wide behaviour (a link to /trial).
      if (!link || link.closest('dialog') || !rootRef.current?.contains(link)) return;
      const href = link.getAttribute('href') ?? '';
      let path = '';
      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return;
        path = url.pathname;
      } catch {
        return;
      }
      if (!TRIAL_PATH.test(path)) return;
      event.preventDefault();
      event.stopPropagation();
      openTrial();
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [openTrial]);

  // ---- scroll reveal ([data-reveal] and the children of [data-reveal-group], staggered) ----
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    root.querySelectorAll<HTMLElement>('[data-reveal-group]').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        if (!(child instanceof HTMLElement)) return;
        child.style.setProperty('--reveal-i', String(index));
        items.push(child);
      });
    });
    const visible = cls('is-visible');
    const show = (element: Element) => element.classList.add(visible);
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      items.forEach(show);
      return undefined;
    }
    root.classList.add(cls('np-js'));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -12% 0px' }
    );
    items.forEach((element) => observer.observe(element));
    // one delayed sweep: anything the observer has not reported is shown if it is on screen
    const sweep = window.setTimeout(() => {
      items.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) show(element);
      });
    }, 900);
    return () => {
      window.clearTimeout(sweep);
      observer.disconnect();
    };
  }, []);

  const values = watch();
  const errorCount = FIELD_NAMES.filter((field) => errors[field]).length;
  const fieldError = (field: FieldName) => errors[field]?.message;
  const isValid = (field: FieldName) => !!touchedFields[field] && !errors[field] && (values[field] ?? '').trim().length > 0;

  const signupPanel = (
    <div className='relative'>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      {user ? (
        <div className={m('np-form')}>
          <AlreadyLoggedInNotice trialConfigId={trialConfigId} trialPageId={trialPageId} />
        </div>
      ) : (
        <form className={m('np-form')} id={FORM_ID} method='post' data-trial-key='matrix' noValidate autoComplete='on' aria-labelledby='np-form-title' onSubmit={handleSubmit(onSubmit)}>
          <div className={m('np-form__head')}>
            <h2 className={m('np-form__title')} id='np-form-title'>
              <Trans t={t} i18nKey='lp.form.title' components={{ hl: <span className={m('np-form__hl')} /> }} />
            </h2>
            <p className={m('np-form__sub')}>
              <Trans t={t} i18nKey='lp.form.sub' components={{ dot: <span className={m('dot')} aria-hidden='true' /> }} />
            </p>
          </div>

          {popUp.show && (
            <div className={m('np-form__alert')}>
              <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />
            </div>
          )}

          <div className={m('np-form__sso')}>
            <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={{ custom: trialConfigId, trialPageId }} />
          </div>

          <div className={m('np-form__fields')}>
            <SignupField id='np-name' label={t('lp.form.label.name')} placeholder={t('lp.form.placeholder.name')} registration={register('name')} error={fieldError('name')} valid={isValid('name')} autoComplete='name' maxLength={255} />
            <div className={m('np-form__row np-form__row--2')}>
              <SignupField id='np-email' label={t('lp.form.label.email')} placeholder={t('lp.form.placeholder.email')} registration={register('email')} error={fieldError('email')} valid={isValid('email')} autoComplete='email' inputMode='email' maxLength={255} />
              <SignupField id='np-email2' label={t('lp.form.label.repeatEmail')} placeholder={t('lp.form.placeholder.repeatEmail')} registration={register('repeatEmail')} error={fieldError('repeatEmail')} valid={isValid('repeatEmail')} autoComplete='email' inputMode='email' maxLength={255} />
            </div>
            <div className={m('np-form__row np-form__row--2')}>
              <SignupField id='np-pass' label={t('lp.form.label.password')} placeholder={t('lp.form.placeholder.password')} registration={register('password')} error={fieldError('password')} valid={isValid('password')} autoComplete='new-password' hint={t('lp.form.passHint')} password={{ show: t('lp.form.showPassword'), hide: t('lp.form.hidePassword') }} />
              <SignupField id='np-pass2' label={t('lp.form.label.passwordRepeat')} placeholder={t('lp.form.placeholder.passwordRepeat')} registration={register('passwordRepeat')} error={fieldError('passwordRepeat')} valid={isValid('passwordRepeat')} autoComplete='new-password' password={{ show: t('lp.form.showPasswordRepeat'), hide: t('lp.form.hidePasswordRepeat') }} />
            </div>
          </div>

          <div className={m('np-form__captcha')}>
            <CaptchaProvider theme='dark' onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} />
            {errors.recaptcha && <span className={m('np-field__msg is-error')}>{errors.recaptcha.message}</span>}
          </div>

          <div className={m(cx('np-form__status np-form__status--top', isSubmitted && errorCount > 0 && 'is-visible'))} data-state={isSubmitted && errorCount > 0 ? 'error' : undefined} aria-live='polite'>
            <div>
              {isSubmitted && errorCount > 0 && (
                <>
                  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                    <circle cx='12' cy='12' r='9' />
                    <path d='M12 7.8v4.9' />
                    <path d='M12 16.2h.01' />
                  </svg>
                  <span>{t('lp.form.fix', { count: errorCount })}</span>
                </>
              )}
            </div>
          </div>

          <button className={m(cx('btn btn-primary btn--block np-form__submit', loading && 'is-busy'))} type='submit' disabled={isDisabledButton || loading} aria-busy={loading ? 'true' : undefined}>
            <span className={m('np-form__submit-label')}>
              {loading ? (
                t('lp.form.busy')
              ) : (
                <>
                  {t('lp.form.submitStart')}{' '}
                  <span className={m('np-form__submit-end')}>
                    {t('lp.form.submitEnd')}
                    <svg className={m('np-form__submit-arrow')} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
                      <path d='M5 12h14' />
                      <path d='m13 6 6 6-6 6' />
                    </svg>
                  </span>
                </>
              )}
            </span>
            <svg className={m('np-form__submit-spin')} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' aria-hidden='true' focusable='false'>
              <path d='M12 3a9 9 0 1 1-6.4 2.6' opacity='.35' />
              <path d='M12 3a9 9 0 0 1 9 9' />
            </svg>
          </button>

          <ul className={m('np-form__games')} aria-label={t('lp.form.gamesLabel')}>
            {FORM_GAMES.map((game) => (
              <li key={game}>{game}</li>
            ))}
          </ul>

          <p className={m('np-form__legal')}>
            <Trans
              t={t}
              i18nKey='lp.form.legal'
              components={{
                terms: <Link href='/terms/terms-of-use' target='_blank' rel='noopener' />,
                privacy: <Link href='/terms/privacy-policy' target='_blank' rel='noopener' />,
              }}
            />
          </p>
        </form>
      )}
    </div>
  );

  // Everything under the hero is static for the form: memoised so a keystroke re-renders only the card.
  const sections = useMemo(
    () => (
      <>
        <ProofClips onTrialClick={handleTrialClick} />
        <Multiconnection onTrialClick={handleTrialClick} />
        <Games onTrialClick={handleTrialClick} />
        <Servers />
        <Pros />
        <Download onTrialClick={handleTrialClick} downloadUrl={urlDownload} onDownloadClick={handleDownloadClick} />
        <Faq />
        <Closing onTrialClick={handleTrialClick} />
      </>
    ),
    [handleTrialClick, urlDownload, handleDownloadClick]
  );

  const rootStyle = { '--font-sans': bai_jamjuree.style?.fontFamily } as CSSProperties;

  return (
    <>
      {/* outside the page root: the site header keeps its own styles, untouched by the LP's element defaults */}
      <Header variant='primary' />
      <div ref={rootRef} className={`${styles.root ?? ''} ${bai_jamjuree.className}`} style={rootStyle}>
        <main id='conteudo'>
          <Hero
            formSlot={
              <div ref={heroSlotRef} style={dialogOpen && heroSlotHeight ? { minHeight: heroSlotHeight } : undefined}>
                {dialogOpen ? null : signupPanel}
              </div>
            }
          />
          {sections}
        </main>
        <MatrixFooter />
        <TrialDialog open={dialogOpen} onClose={closeDialog}>
          {signupPanel}
        </TrialDialog>
      </div>
    </>
  );
};
